'use client';

import { type Country, type CountryVariantTemplate, useCountries, useUserCountryQueries } from '@capsicum/common';
import { useCallback, useEffect, useState } from 'react';

import { Resource } from '../../../atoms/resource/Resource';
import { BaseSelectInput, type BaseSelectInputProps } from '../select-input/BaseSelectInput';

import { BaseCountrySelectInputSkeleton, BaseCountrySelectTemplate, CountryCodeWrapper, GetCountryFlag } from './';

export type BaseCountryInputProps = Omit<
	BaseSelectInputProps<Country>,
	'customLabelProp' | 'data' | 'itemTemplate' | 'onSearch'
> & {
	variant?: CountryVariantTemplate;
	withoutInitialValue?: boolean;
};

export const BaseCountrySelect = ({
	labelProp,
	onChange,
	tabIndex,
	value,
	variant = 'country',
	withoutInitialValue,
	...props
}: BaseCountryInputProps) => {
	const { countriesQuery } = useCountries();

	const userCountry = useUserCountryQueries();

	const [selectedItem, setSelectedItem] = useState<Country | undefined>(undefined);

	useEffect(() => {
		if (!value) {
			setSelectedItem(undefined);
		}
		if (value) {
			setSelectedItem(value);
		} else if (!withoutInitialValue && !selectedItem) {
			const initialValue = userCountry ?? countriesQuery?.data?.[0];

			setSelectedItem(initialValue);
		}
	}, [value, withoutInitialValue, userCountry, countriesQuery?.data, selectedItem, onChange]);

	const itemTemplate = useCallback(
		({ item }: { item: Country }) => {
			return <BaseCountrySelectTemplate item={item} variant={variant} />;
		},
		[variant]
	);

	const countryListLabelProp = useCallback(
		(item: Country) => (
			<CountryCodeWrapper>
				<GetCountryFlag iconSize={16} item={item} />
				{variant === 'countryWithCodeAndFlag' ? item.internationalCallingCode : item.name}
			</CountryCodeWrapper>
		),
		[variant]
	);

	const onSearch = useCallback((data: Array<Country>, text: string) => {
		return data.filter(
			({ internationalCallingCode, iso2, name }) =>
				iso2?.toLowerCase().includes(text.toLowerCase()) ||
				internationalCallingCode?.toLowerCase().replace('+', '').startsWith(text.replace('+', '')) ||
				name?.toLowerCase().includes(text.toLowerCase())
		);
	}, []);

	return (
		<Resource r={countriesQuery} skeleton={<BaseCountrySelectInputSkeleton />}>
			{data => (
				<BaseSelectInput
					customLabelProp={
						['countryWithCodeAndFlag', 'countryWithFlag'].includes(variant)
							? countryListLabelProp
							: undefined
					}
					data={data}
					itemTemplate={itemTemplate}
					labelProp={labelProp}
					tabIndex={tabIndex}
					value={selectedItem}
					onChange={onChange}
					onSearch={onSearch}
					{...props}
				/>
			)}
		</Resource>
	);
};
